import { wrapsOnlyStyledLink } from "../html-parse";

/**
 * Which elements the fallback tier claims.
 *
 * ProseMirror deletes an element no rule matches and carries on parsing its
 * children. That is why a designed email arrived stripped: the `<div>` holding
 * the page to 600px, the `<section>` painting a band, the `<style>` block
 * carrying half the design — all dropped, with only the text inside them left.
 *
 * The tier sits *underneath* the typed blocks. Every rule here runs at a
 * priority below theirs, so it only sees an element after every typed block has
 * had its turn — including the ones that looked and declined. A `<p>` reaches
 * `htmlText` exactly when `emailParagraph` decided it could not reproduce it.
 *
 * So what this file declines is narrower than "what a typed block usually
 * takes". It is the elements that would be *wrong* to preserve.
 */

/**
 * Elements whose meaning depends on a table we do not re-emit.
 *
 * A `<tr>` outside a `<table>` is not markup at all. The grid owns these, and
 * where it declines one — a wrapper row, a button's frame — the right answer is
 * still to walk through it rather than preserve it.
 */
const TABLE_TAGS = new Set([
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "td",
  "th",
  "caption",
  "colgroup",
  "col",
]);

/**
 * Elements a typed block owns outright.
 *
 * Void and list elements: there is no node in this tier that could hold one, so
 * the typed block has to claim them whatever they carry.
 */
const MODELLED_TAGS = new Set(["img", "hr", "br", "ul", "ol", "li"]);

/** Elements whose content is text rather than markup — `htmlRaw`'s business. */
const RAW_TAGS = new Set(["style", "script", "noscript"]);

/** Document scaffolding, which has no place in a body fragment. */
const DOCUMENT_TAGS = new Set([
  "html",
  "head",
  "body",
  "meta",
  "title",
  "base",
  "link",
]);

/**
 * Inline-level elements.
 *
 * These stay transparent. Claiming one would turn a run of text inside a
 * paragraph into a block node, and ProseMirror would split the paragraph to
 * make room for it. Their formatting is a mark's job, not a node's.
 */
export const INLINE_TAGS = new Set([
  "a",
  "span",
  "b",
  "strong",
  "i",
  "em",
  "u",
  "s",
  "strike",
  "del",
  "ins",
  "code",
  "kbd",
  "samp",
  "var",
  "sub",
  "sup",
  "small",
  "big",
  "tt",
  "font",
  "abbr",
  "acronym",
  "cite",
  "q",
  "mark",
  "bdi",
  "bdo",
  "ruby",
  "rt",
  "rp",
  "wbr",
  "label",
  "output",
  "time",
  "data",
  "nobr",
]);

/** Interactive and embedded content, which no mail client renders anyway. */
const UNSUPPORTED_TAGS = new Set([
  "form",
  "input",
  "textarea",
  "select",
  "option",
  "optgroup",
  "button",
  "iframe",
  "object",
  "embed",
  "param",
  "video",
  "audio",
  "source",
  "track",
  "canvas",
  "svg",
  "math",
  "map",
  "area",
  "picture",
  "slot",
  "template",
]);

/**
 * A tag name we are willing to re-emit. Namespaced tags are included on
 * purpose: `<o:p>` and `<v:roundrect>` are what Word and Outlook exports are
 * made of, and they are exactly the elements that vanish today.
 */
const TAG_NAME = /^[a-z][a-z0-9-]*(?::[a-z][a-z0-9-]*)?$/;

/** Elements whose content is inline, so a block node cannot live inside them. */
const TEXT_BLOCK_SELECTOR = "p, h1, h2, h3, h4, h5, h6, blockquote, a";

/** Tags that start a new line — the test for structure versus a run of text. */
const BLOCK_LEVEL_TAGS = new Set([
  ...Array.from(TABLE_TAGS),
  "address",
  "article",
  "aside",
  "blockquote",
  "center",
  "dd",
  "details",
  "dialog",
  "div",
  "dl",
  "dt",
  "fieldset",
  "figcaption",
  "figure",
  "footer",
  "form",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "hgroup",
  "hr",
  "li",
  "main",
  "nav",
  "ol",
  "p",
  "pre",
  "section",
  "summary",
  "ul",
]);

/** True when the element holds structure rather than a run of text. */
export function hasBlockLevelChild(element: HTMLElement): boolean {
  return Array.from(element.children).some((child) =>
    BLOCK_LEVEL_TAGS.has(child.tagName.toLowerCase()),
  );
}

/**
 * The tag the fallback tier should re-emit for this element, or `null` when it
 * declines.
 */
export function claimableTag(element: HTMLElement): string | null {
  const tag = element.tagName.toLowerCase();
  if (
    TABLE_TAGS.has(tag) ||
    MODELLED_TAGS.has(tag) ||
    RAW_TAGS.has(tag) ||
    DOCUMENT_TAGS.has(tag) ||
    INLINE_TAGS.has(tag) ||
    UNSUPPORTED_TAGS.has(tag) ||
    !TAG_NAME.test(tag)
  ) {
    return null;
  }
  // Only in a position where a block node can actually live. Inside a
  // paragraph or a heading there is nowhere for one to go, and ProseMirror
  // would break the text block apart to make room.
  if (element.parentElement?.closest(TEXT_BLOCK_SELECTOR)) return null;
  // An element whose whole content is a call-to-action is that button's frame
  // rather than a container of its own. `emailParagraph` declines one so the
  // button rule can reach the link inside; claiming it here would put the
  // paragraph back and leave the button as underlined text.
  if (wrapsOnlyStyledLink(element)) return null;
  return tag;
}
